import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Gift, Calendar, Users, Trophy, Circle as XCircle, CircleCheck as CheckCircle, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "../contexts/AuthContext";

interface Vencedor {
  usuario_id: string;
  usuario: { nome: string };
}

interface Brinde {
  id: string;
  titulo: string;
  descricao: string;
  data_sorteio: string;
  quantidade: number;
  status: "ativo" | "sorteado" | "cancelado";
  vencedor_id: string | null;
  vencedor?: { nome: string };
  vencedores?: Vencedor[];
  total_inscricoes?: number;
  usuario_inscrito?: boolean;
}

export default function SorteioBrindes() {
  const { user } = useAuth();
  const [brindes, setBrindes] = useState<Brinde[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"ativos" | "sorteados" | "todos">("ativos");
  const [processando, setProcessando] = useState<string | null>(null);
  const [modalBrinde, setModalBrinde] = useState<Brinde | null>(null);

  useEffect(() => {
    loadBrindes();
  }, [filter, user?.dbId]);

  const loadBrindes = async () => {
    setLoading(true);

    let query = supabase
      .from("sorteio_brindes")
      .select(`*, vencedor:vencedor_id(nome)`)
      .order("data_sorteio", { ascending: filter === "ativos" });

    if (filter === "ativos") query.eq("status", "ativo");
    if (filter === "sorteados") query.eq("status", "sorteado");

    const { data, error } = await query;

    if (!error && data) {
      const final = await Promise.all(
        data.map(async (item: Brinde) => {
          const { count } = await supabase
            .from("sorteio_inscricoes")
            .select("*", { count: "exact", head: true })
            .eq("brinde_id", item.id);

          const { data: exists } = await supabase
            .from("sorteio_inscricoes")
            .select("id")
            .eq("brinde_id", item.id)
            .eq("usuario_id", user?.dbId)
            .maybeSingle();

          const { data: vencedores, error: vencedoresError } = await supabase
            .from("sorteio_vencedores")
            .select("usuario_id, usuario:usuario_id(nome)")
            .eq("brinde_id", item.id)
            .order("criado_em", { ascending: true });

          if (vencedoresError) {
            console.error("Erro ao buscar vencedores:", vencedoresError);
          }

          return {
            ...item,
            total_inscricoes: count ?? 0,
            usuario_inscrito: !!exists,
            vencedores: vencedores || [],
          };
        })
      );

      setBrindes(final);
    } else if (error) {
      console.error("Erro ao carregar brindes:", error);
    }

    setLoading(false);
  };

  const toggleInscricao = async (id: string) => {
    if (!user?.dbId) return toast.error("Você precisa estar logado.");
    setProcessando(id);

    const item = brindes.find((b) => b.id === id);
    if (!item) return;

    try {
      if (item.usuario_inscrito) {
        await supabase
          .from("sorteio_inscricoes")
          .delete()
          .eq("brinde_id", id)
          .eq("usuario_id", user.dbId);

        toast.success("Inscrição removida!");
      } else {
        await supabase.from("sorteio_inscricoes").insert({
          brinde_id: id,
          usuario_id: user.dbId,
        });

        toast.success("Inscrição realizada!");
      }
      loadBrindes();
    } finally {
      setProcessando(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="bg-primary text-white p-3 rounded-xl shadow">
            <Gift className="w-7 h-7" />
          </div>
          <div>
            <h1 className="font-display text-headline-lg text-on-surface">Sorteio de Brindes</h1>
            <p className="text-sm text-secondary">Participe dos sorteios disponíveis.</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-3">
          {["ativos", "sorteados", "todos"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`px-5 py-2 rounded-lg font-medium transition ${
                filter === f
                  ? "bg-primary text-white"
                  : "bg-surface-container-low text-on-surface hover:bg-surface-container"
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Lista */}
        {loading ? (
          <p className="text-lg text-secondary">Carregando...</p>
        ) : brindes.length === 0 ? (
          <p className="text-lg text-secondary">Nenhum sorteio encontrado.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brindes.map((b) => (
              <div
                key={b.id}
                className="bg-white dark:bg-gray-800 p-5 rounded-xl hover:shadow-lg transition space-y-4 border border-outline-variant dark:border-gray-700"
              >
                <h2 className="font-semibold text-xl">{b.titulo}</h2>
                <p className="text-secondary text-sm">{b.descricao}</p>

                <div className="text-sm text-secondary flex items-center gap-2">
                  <Calendar className="w-4" />
                  {format(new Date(b.data_sorteio), "dd/MM/yyyy", { locale: ptBR })}
                </div>

                <div className="text-sm flex items-center gap-2">
                  <Users className="w-4" />
                  {b.total_inscricoes} inscritos
                </div>

                <p className="text-sm font-medium">
                  🎁 Quantidade: {b.quantidade} {b.quantidade > 1 ? "vencedores" : "vencedor"}
                </p>

                {b.status === "ativo" && (
                  <button
                    onClick={() => toggleInscricao(b.id)}
                    disabled={processando === b.id}
                    className={`w-full py-2 text-white font-semibold rounded-lg transition ${
                      b.usuario_inscrito ? "bg-error hover:bg-error/90" : "bg-primary hover:bg-primary-dark"
                    }`}
                  >
                    {processando === b.id
                      ? "Processando..."
                      : b.usuario_inscrito
                      ? "Cancelar inscrição"
                      : "Participar"}
                  </button>
                )}

                {b.status === "sorteado" && b.vencedores && b.vencedores.length > 0 && (
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <div className="flex items-center justify-center mb-2">
                      <Trophy className="w-6 h-6 text-yellow-700 mr-2" />
                      <p className="font-semibold text-yellow-900">
                        {b.vencedores.length > 1 ? `${b.vencedores.length} Vencedores` : 'Vencedor'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      {b.vencedores.slice(0, 3).map((vencedor, index) => (
                        <p key={index} className="text-sm text-yellow-800 text-center">
                          {index + 1}. {vencedor.usuario?.nome || 'Desconhecido'}
                        </p>
                      ))}
                      {b.vencedores.length > 3 && (
                        <button
                          onClick={() => setModalBrinde(b)}
                          className="w-full mt-2 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded transition"
                        >
                          Ver todos os {b.vencedores.length} vencedores
                        </button>
                      )}
                      {b.vencedores.length <= 3 && b.vencedores.length > 1 && (
                        <button
                          onClick={() => setModalBrinde(b)}
                          className="w-full mt-2 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded transition"
                        >
                          Ver detalhes
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Vencedores */}
      {modalBrinde && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy className="w-8 h-8 text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">{modalBrinde.titulo}</h2>
                  <p className="text-yellow-100 text-sm">
                    {modalBrinde.vencedores?.length} {modalBrinde.vencedores && modalBrinde.vencedores.length > 1 ? 'Vencedores' : 'Vencedor'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModalBrinde(null)}
                className="text-white hover:bg-yellow-600 p-2 rounded-full transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              <div className="space-y-3">
                {modalBrinde.vencedores?.map((vencedor, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg hover:shadow-md transition border border-outline-variant"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>
                    <div className="flex-grow">
                      <p className="text-lg font-semibold text-on-surface">
                        {vencedor.usuario?.nome || 'Desconhecido'}
                      </p>
                    </div>
                    <Trophy className="w-6 h-6 text-yellow-600" />
                  </div>
                ))}
              </div>
            </div>

            {/* Footer do Modal */}
            <div className="bg-surface-container-low p-4 flex items-center justify-between border-t border-outline-variant">
              <div className="text-sm text-secondary">
                <Calendar className="w-4 h-4 inline mr-1" />
                Sorteado em: {format(new Date(modalBrinde.data_sorteio), "dd/MM/yyyy", { locale: ptBR })}
              </div>
              <button
                onClick={() => setModalBrinde(null)}
                className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
